// BuildingBlockDetailModal.jsx - Updated with proper CSS classes

import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Badge, Tabs, Tab } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import "./ProjectBuildingBlockTab";

const BuildingBlockDetailModal = ({
  show,
  onHide,
  block,
  blockViewType,
  orgId,
  projectId,
  userId,
  contactNamesCache,
  teamMemberDetails,
  clientContactPersons,
  partnerContactPersons,
  resourcesByOrg,
  getTasksByBuildingBlock,
  getTicketsByBuildingBlock,
  getTasksByProject,
  getTicketsByProjectId,
  mapTaskToBuildingBlock,
  mapTicketToBuildingBlock,
  removeSpecificTaskMapping,
  removeTicketMapping,
  onRefresh,
  taskStatusMap = {},
  getAllTaskStatuses,
}) => {
  const [activeTab, setActiveTab] = useState("view");
  const [mappedItems, setMappedItems] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [localTaskStatusMap, setLocalTaskStatusMap] = useState(taskStatusMap);
  const [statusLoading, setStatusLoading] = useState(false);

  // Fetch task statuses if not provided
  useEffect(() => {
    if (
      Object.keys(localTaskStatusMap).length === 0 &&
      getAllTaskStatuses &&
      !statusLoading
    ) {
      const fetchStatuses = async () => {
        setStatusLoading(true);
        try {
          const statuses = await getAllTaskStatuses();
          const map = {};
          statuses.forEach((status) => {
            map[status.statusGuid.toLowerCase()] = status.statusName;
          });
          setLocalTaskStatusMap(map);
        } catch (error) {
          console.error("Failed to fetch task statuses:", error);
        } finally {
          setStatusLoading(false);
        }
      };
      fetchStatuses();
    }
  }, [getAllTaskStatuses, localTaskStatusMap, statusLoading]);

  // Helper function to resolve task status GUID to name
  const getTaskStatusName = (statusGuid) => {
    if (!statusGuid) return "No Status";

    const isGuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        statusGuid,
      );
    if (!isGuid) return statusGuid;

    const lowerGuid = statusGuid.toLowerCase();
    return localTaskStatusMap[lowerGuid] || statusGuid.substring(0, 8);
  };

  // Helper function to resolve ticket status
  const getTicketStatusName = (status) => {
    if (!status) return "N/A";

    const isGuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        status,
      );
    if (!isGuid) return status;

    return status.substring(0, 8);
  };

  // Load both mapped and available items
  const loadData = async () => {
    if (!block) return;

    setLoading(true);
    try {
      let mapped;
      if (blockViewType === "TASK") {
        mapped = await getTasksByBuildingBlock(block.buildingBlockId);
        mapped = (mapped || []).filter(
          (item) =>
            item.isActive === true ||
            item.isActive === 1 ||
            item.isActive === "1",
        );
      } else {
        mapped = await getTicketsByBuildingBlock(block.buildingBlockId);
        mapped = (mapped || []).filter((item) => item.isActive !== false);
      }
      setMappedItems(mapped);

      let allItems;
      if (blockViewType === "TASK") {
        allItems = await getTasksByProject(projectId);
        const mappedIds = new Set(mapped.map((item) => item.taskId));
        allItems = (allItems || []).filter(
          (item) => !mappedIds.has(item.taskId),
        );
      } else {
        allItems = await getTicketsByProjectId(projectId);
        const mappedIds = new Set(mapped.map((item) => item.ticketId));
        allItems = (allItems || []).filter(
          (item) => !mappedIds.has(item.ticketId),
        );
      }
      setAvailableItems(allItems);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && block) {
      loadData();
      setSelectedItemIds([]);
    }
  }, [show, block, blockViewType]);

  // Helper function to resolve contact GUID to name
  const resolveContactGuidToName = (guid) => {
    if (!guid) return "-";

    const isGuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        guid,
      );

    if (!isGuid) return guid;

    if (contactNamesCache && contactNamesCache[guid.toLowerCase()]) {
      return contactNamesCache[guid.toLowerCase()];
    }

    const teamMember = teamMemberDetails?.find(
      (user) => user.contactGuid?.toLowerCase() === guid.toLowerCase(),
    );
    if (teamMember) return teamMember.username;

    const clientContact = clientContactPersons?.find(
      (c) => c.contactGuid?.toLowerCase() === guid.toLowerCase(),
    );
    if (clientContact) return clientContact.contactPerName;

    const partnerContact = partnerContactPersons?.find(
      (c) => c.contactGuid?.toLowerCase() === guid.toLowerCase(),
    );
    if (partnerContact) return partnerContact.contactPerName;

    const resource = resourcesByOrg?.find(
      (res) => res.contactGuid?.toLowerCase() === guid.toLowerCase(),
    );
    if (resource) return resource.name;

    return guid.substring(0, 8);
  };

  // Handle mapping selected items
  const handleMapItems = async () => {
    if (selectedItemIds.length === 0) {
      toast.error("Please select at least one item to map");
      return;
    }

    setMappingLoading(true);
    try {
      for (const id of selectedItemIds) {
        if (blockViewType === "TASK") {
          await mapTaskToBuildingBlock({
            buildingBlockId: block.buildingBlockId,
            taskId: id,
            orgId: String(orgId),
            createdBy: String(userId),
          });
        } else {
          await mapTicketToBuildingBlock({
            buildingBlockId: block.buildingBlockId,
            ticketId: id,
            orgId: String(orgId),
            createdBy: String(userId),
          });
        }
      }

      toast.success(`${selectedItemIds.length} item(s) mapped successfully`);
      setSelectedItemIds([]);
      await loadData();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error mapping items:", error);
      toast.error("Failed to map items");
    } finally {
      setMappingLoading(false);
    }
  };

  // Handle removing a mapped item
  const handleRemoveItem = async (itemId, itemName) => {
    if (!window.confirm(`Remove "${itemName}" from this building block?`)) {
      return;
    }

    setMappingLoading(true);
    try {
      if (blockViewType === "TASK") {
        await removeSpecificTaskMapping(itemId, block.buildingBlockId);
      } else {
        await removeTicketMapping(itemId, block.buildingBlockId);
      }

      toast.success("Item removed successfully");
      await loadData();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error removing item:", error);
      toast.error("Failed to remove item");
    } finally {
      setMappingLoading(false);
    }
  };

  // Toggle selection of available items
  const toggleSelectItem = (id) => {
    setSelectedItemIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  // Select/Deselect all available items
  const toggleSelectAll = () => {
    if (selectedItemIds.length === availableItems.length) {
      setSelectedItemIds([]);
    } else {
      setSelectedItemIds(
        availableItems.map((item) =>
          blockViewType === "TASK" ? item.taskId : item.ticketId,
        ),
      );
    }
  };

  // Get status badge class based on status name
  const getStatusBadgeClass = (statusName) => {
    const statusMap = {
      Completed: "success",
      "In Progress": "warning",
      Open: "info",
      Closed: "secondary",
      Pending: "warning",
      "Not Started": "secondary",
      "On Hold": "danger",
      Cancelled: "danger",
      Approved: "success",
      Rejected: "danger",
    };
    return statusMap[statusName] || "secondary";
  };

  // Get display status text
  const getDisplayStatus = (item) => {
    const isTask = blockViewType === "TASK";

    if (isTask) {
      const statusGuid = item.taskStatus;
      return getTaskStatusName(statusGuid);
    } else {
      const status = item.status || item.ticketStatus;
      return getTicketStatusName(status);
    }
  };

  // Render a mapped item row
  const renderMappedItem = (item) => {
    const isTask = blockViewType === "TASK";
    const itemId = isTask ? item.taskId : item.ticketId;
    const itemName = isTask ? item.taskName : item.ticketName;
    const displayStatus = getDisplayStatus(item);
    const assignee = isTask ? item.assignedTo : null;

    return (
      <tr key={itemId}>
        <td className="ptbb-dm-item-id">{itemId}</td>
        <td className="ptbb-dm-item-name">{itemName}</td>
        <td>
          <Badge
            bg={getStatusBadgeClass(displayStatus)}
            className="ptbb-dm-badge"
          >
            {displayStatus}
          </Badge>
        </td>
        {isTask && (
          <td>
            {assignee ? resolveContactGuidToName(assignee) : "Unassigned"}
          </td>
        )}
        <td>
          <Button
            size="sm"
            variant="outline-danger"
            className="ptbb-dm-action-btn ptbb-dm-action-btn-danger"
            onClick={() => handleRemoveItem(itemId, itemName)}
            disabled={mappingLoading}
          >
            <i className="fas fa-trash"></i> Remove
          </Button>
        </td>
      </tr>
    );
  };

  // Render available item row for mapping
  const renderAvailableItem = (item) => {
    const isTask = blockViewType === "TASK";
    const itemId = isTask ? item.taskId : item.ticketId;
    const itemName = isTask ? item.taskName : item.ticketName;
    const displayStatus = getDisplayStatus(item);

    return (
      <tr key={itemId}>
        <td style={{ width: "40px" }}>
          <Form.Check
            type="checkbox"
            className="ptbb-dm-checkbox"
            checked={selectedItemIds.includes(itemId)}
            onChange={() => toggleSelectItem(itemId)}
          />
        </td>
        <td className="ptbb-dm-item-id">{itemId}</td>
        <td className="ptbb-dm-item-name">{itemName}</td>
        <td>
          <Badge
            bg={getStatusBadgeClass(displayStatus)}
            className="ptbb-dm-badge"
          >
            {displayStatus}
          </Badge>
        </td>
        {isTask && (
          <td>
            {item.assignedTo
              ? resolveContactGuidToName(item.assignedTo)
              : "Unassigned"}
          </td>
        )}
      </tr>
    );
  };

  // Get block status display name
  const getBlockStatusDisplayName = () => {
    const status = block.statusName || block.buildingBlockStatus;
    if (!status) return "Not Started";

    const isGuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        status,
      );
    if (!isGuid) return status;

    return status;
  };

  if (!block) return null;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="ptbb-dm-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-cube me-2"></i>
          {block.buildingBlockName}
          <small className="text-muted ms-2" style={{ fontSize: "12px" }}>
            {block.buildingBlockId}
          </small>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Block Information Summary */}
        <div className="ptbb-dm-summary">
          <div className="ptbb-dm-summary-row">
            <div className="ptbb-dm-summary-col">
              <div className="ptbb-dm-summary-item">
                <strong>Status:</strong>
                <Badge
                  bg={getStatusBadgeClass(getBlockStatusDisplayName())}
                  className="ptbb-dm-badge ms-2"
                >
                  {getBlockStatusDisplayName()}
                </Badge>
              </div>
            </div>
            <div className="ptbb-dm-summary-col">
              <div className="ptbb-dm-summary-item">
                <strong>Type:</strong>
                <span className="ms-2">
                  {block.typeName || "Not specified"}
                </span>
              </div>
            </div>
          </div>

          {block.buildingBlockDescription && (
            <div className="ptbb-dm-summary-description">
              <strong>Description:</strong>
              <p className="mt-1 mb-0">{block.buildingBlockDescription}</p>
            </div>
          )}

          <div className="ptbb-dm-summary-row mt-2">
            {block.buildingBlockProcessPoc && (
              <div className="ptbb-dm-summary-col">
                <div className="ptbb-dm-summary-item">
                  <strong>Process POC:</strong>
                  <span className="ms-2">
                    {resolveContactGuidToName(block.buildingBlockProcessPoc)}
                  </span>
                </div>
              </div>
            )}
            {block.buildingBlockTechPoc && (
              <div className="ptbb-dm-summary-col">
                <div className="ptbb-dm-summary-item">
                  <strong>Tech POC:</strong>
                  <span className="ms-2">
                    {resolveContactGuidToName(block.buildingBlockTechPoc)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {(block.actualStartDate || block.actualEndDate) && (
            <div className="ptbb-dm-summary-dates">
              {block.actualStartDate && (
                <span>
                  <i className="fas fa-calendar-alt"></i>
                  Start: {moment(block.actualStartDate).format("MMM DD, YYYY")}
                </span>
              )}
              {block.actualEndDate && (
                <span>
                  <i className="fas fa-calendar-check"></i>
                  End: {moment(block.actualEndDate).format("MMM DD, YYYY")}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Tabs for View and Map */}
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="ptbb-dm-tabs"
        >
          <Tab
            eventKey="view"
            title={
              <span>
                <i className="fas fa-list me-1"></i> View Items (
                {mappedItems.length})
              </span>
            }
          >
            <div className="ptbb-dm-table-container">
              {loading ? (
                <div className="ptbb-dm-loading">
                  <div className="ptbb-dm-spinner"></div>
                  <p>Loading items...</p>
                </div>
              ) : mappedItems.length === 0 ? (
                <div className="ptbb-dm-empty">
                  <div className="ptbb-dm-empty-icon">
                    <i className="fas fa-inbox"></i>
                  </div>
                  <p>
                    No {blockViewType.toLowerCase()}s mapped to this building
                    block yet.
                  </p>
                  <Button
                    variant="primary"
                    size="sm"
                    className="ptbb-dm-outline-btn"
                    onClick={() => setActiveTab("map")}
                  >
                    <i className="fas fa-plus"></i> Map Items
                  </Button>
                </div>
              ) : (
                <table className="table table-bordered table-hover ptbb-dm-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Status</th>
                      {blockViewType === "TASK" && <th>Assignee</th>}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>{mappedItems.map(renderMappedItem)}</tbody>
                </table>
              )}
            </div>
          </Tab>

          <Tab
            eventKey="map"
            title={
              <span>
                <i className="fas fa-plus-circle me-1"></i> Map Items
              </span>
            }
          >
            <div className="ptbb-dm-table-container">
              {loading ? (
                <div className="ptbb-dm-loading">
                  <div className="ptbb-dm-spinner"></div>
                  <p>Loading available items...</p>
                </div>
              ) : availableItems.length === 0 ? (
                <div className="ptbb-dm-empty">
                  <div className="ptbb-dm-empty-icon ptbb-dm-empty-success">
                    <i className="fas fa-check-circle"></i>
                  </div>
                  <p>
                    All {blockViewType.toLowerCase()}s are already mapped to
                    this building block!
                  </p>
                </div>
              ) : (
                <>
                  <div className="ptbb-dm-map-controls">
                    <div className="ptbb-dm-select-controls">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="ptbb-dm-outline-btn"
                        onClick={toggleSelectAll}
                      >
                        {selectedItemIds.length === availableItems.length
                          ? "Deselect All"
                          : "Select All"}
                      </Button>
                      <span className="ptbb-dm-select-count">
                        {selectedItemIds.length} of {availableItems.length}{" "}
                        selected
                      </span>
                    </div>
                    <Button
                      variant="primary"
                      className="ptbb-dm-map-btn"
                      onClick={handleMapItems}
                      disabled={selectedItemIds.length === 0 || mappingLoading}
                    >
                      {mappingLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Mapping...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-link me-1"></i>
                          Map Selected ({selectedItemIds.length})
                        </>
                      )}
                    </Button>
                  </div>

                  <table className="table table-bordered table-hover ptbb-dm-table">
                    <thead>
                      <tr>
                        <th style={{ width: "40px" }}>Select</th>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Status</th>
                        {blockViewType === "TASK" && <th>Assignee</th>}
                      </tr>
                    </thead>
                    <tbody>{availableItems.map(renderAvailableItem)}</tbody>
                  </table>
                </>
              )}
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
          className="ptbb-dm-outline-btn"
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BuildingBlockDetailModal;
